import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", roleId: "5", roleName: "Customer" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to SOS");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-emerald-base flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 text-emerald-muted hover:text-gold transition" data-testid="brand-link">
          <Sparkles className="w-4 h-4 text-gold" />
          <span className="font-heading text-2xl text-white">SOS</span>
        </Link>
        <div className="overline mb-3">Create Guest Profile</div>
        <h1 className="font-heading text-4xl text-white mb-3">Begin your ritual.</h1>
        <p className="text-emerald-muted mb-8 text-sm">A few details, and you'll be ready to book.</p>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <Label className="text-emerald-muted text-xs uppercase tracking-widest">Full Name</Label>
            <Input required data-testid="register-name-input" value={form.name} onChange={set("name")}
              className="mt-2 bg-emerald-surface border-gold-subtle text-white focus:border-gold h-12" placeholder="Your name" />
          </div>
          <div>
            <Label className="text-emerald-muted text-xs uppercase tracking-widest">Email</Label>
            <Input type="email" data-testid="register-email-input" value={form.email} onChange={set("email")}
              className="mt-2 bg-emerald-surface border-gold-subtle text-white focus:border-gold h-12" placeholder="you@example.com" />
          </div>
          <div>
            <Label className="text-emerald-muted text-xs uppercase tracking-widest">Phone (optional)</Label>
            <Input data-testid="register-phone-input" value={form.phone} onChange={set("phone")}
              className="mt-2 bg-emerald-surface border-gold-subtle text-white focus:border-gold h-12" placeholder="9941034123" />
          </div>
          <div>
            <Label className="text-emerald-muted text-xs uppercase tracking-widest">Password</Label>
            <Input required type="password" minLength={6} data-testid="register-password-input" value={form.password} onChange={set("password")}
              className="mt-2 bg-emerald-surface border-gold-subtle text-white focus:border-gold h-12" placeholder="At least 6 characters" />
          </div>
          <div>
            <Label className="text-emerald-muted text-xs uppercase tracking-widest">Role</Label>
            <Input value="Customer" disabled
              className="mt-2 bg-emerald-surface border-gold-subtle text-white h-12" />
          </div>
          <button type="submit" disabled={loading} data-testid="register-submit-btn"
            className="gold-button w-full h-12 rounded-md disabled:opacity-60 mt-2">{loading ? "Creating…" : "Create Profile"}</button>
        </form>

        <p className="text-sm text-emerald-muted mt-8">
          Already a guest? <Link to="/login" className="text-gold hover:underline" data-testid="link-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
